import React, { useState, useEffect, useCallback } from 'react';

const WORKER_URL = 'https://rakeen-whatsapp-poc.thamer-21d.workers.dev';

const STATUS_LABELS = {
  inquiry: 'استفسار',
  quoted: 'عرض سعر',
  confirmed: 'تأكيد',
  processing: 'تجهيز',
  ready: 'جاهز',
  delivered: 'تسليم',
};

function StatsPage({ idToken, onNavigate }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchOrders = useCallback(async () => {
    try {
      const res = await fetch(`${WORKER_URL}/api/conversations`, {
        cache: 'no-store',
        headers: { 'Authorization': `Bearer ${idToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        setOrders(data.filter((o) => !o.isTest));
        setLoading(false);
      }
    } catch (err) {
      setLoading(false);
    }
  }, [idToken]);

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 10000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  if (loading) {
    return <div className="sd-stats-loading">جاري التحميل...</div>;
  }

  // Categorize
  const quotes = orders.filter((o) => o.tag && o.tag.includes('عرض سعر'));
  const inquiries = orders.filter((o) => o.tag && o.tag.includes('استفسار'));
  const maintenance = orders.filter((o) => o.tag && o.tag.includes('صيانة'));
  const generalChats = orders.filter((o) => !o.tag || (!o.tag.includes('عرض سعر') && !o.tag.includes('استفسار') && !o.tag.includes('صيانة')));

  // Stats
  const total = orders.length;
  const today = new Date().toISOString().split('T')[0];
  const todayCount = orders.filter((o) => o.createdAt && o.createdAt.startsWith(today)).length;
  const delivered = orders.filter((o) => o.status === 'delivered').length;

  // Last message helper
  const lastMsg = (conv) => {
    if (!conv.messages || conv.messages.length === 0) return '—';
    const msg = conv.messages[conv.messages.length - 1].content;
    return msg.length > 40 ? msg.substring(0, 40) + '...' : msg;
  };

  return (
    <div className="sd-stats">
      {/* KPI Cards */}
      <div className="sd-stats-grid">
        <div className="sd-stat-card">
          <span className="sd-stat-number">{total}</span>
          <span className="sd-stat-label">الإجمالي</span>
        </div>
        <div className="sd-stat-card">
          <span className="sd-stat-number">{todayCount}</span>
          <span className="sd-stat-label">اليوم</span>
        </div>
        <div className="sd-stat-card sd-stat-inquiry">
          <span className="sd-stat-number">{inquiries.length}</span>
          <span className="sd-stat-label">استفسارات</span>
        </div>
        <div className="sd-stat-card sd-stat-warning">
          <span className="sd-stat-number">{quotes.length}</span>
          <span className="sd-stat-label">طلبات أسعار</span>
        </div>
        <div className="sd-stat-card sd-stat-maintenance">
          <span className="sd-stat-number">{maintenance.length}</span>
          <span className="sd-stat-label">صيانة</span>
        </div>
        <div className="sd-stat-card sd-stat-success">
          <span className="sd-stat-number">{delivered}</span>
          <span className="sd-stat-label">تم التسليم</span>
        </div>
      </div>

      {/* Quote Orders */}
      <div className="sd-stats-section">
        <div className="sd-stats-section-header">
          <h3>📋 طلبات أسعار ({quotes.length})</h3>
          <button className="sd-stats-view-all" onClick={() => onNavigate('orders')}>عرض الكل ←</button>
        </div>
        {quotes.length === 0 ? (
          <div className="sd-stats-empty">لا توجد طلبات أسعار</div>
        ) : (
          <div className="sd-stats-table">
            <div className="sd-stats-table-header">
              <span>الرقم</span>
              <span>المنتج</span>
              <span>الحالة</span>
              <span>التاريخ</span>
            </div>
            {quotes.slice(0, 5).map((order) => (
              <div key={order.id} className="sd-stats-table-row" onClick={() => onNavigate('conversations')}>
                <span className="sd-stats-order-id">#{order.id}</span>
                <span className="sd-stats-order-product">{order.product || '—'}</span>
                <span className={`sd-stats-order-status sd-status-${order.status}`}>
                  {STATUS_LABELS[order.status] || order.status}
                </span>
                <span className="sd-stats-order-date">
                  {order.createdAt ? new Date(order.createdAt).toLocaleDateString('ar-SA') : '—'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Inquiries */}
      <div className="sd-stats-section">
        <div className="sd-stats-section-header">
          <h3>🔍 استفسارات ({inquiries.length})</h3>
          <button className="sd-stats-view-all" onClick={() => onNavigate('conversations')}>عرض الكل ←</button>
        </div>
        {inquiries.length === 0 ? (
          <div className="sd-stats-empty">لا توجد استفسارات</div>
        ) : (
          <div className="sd-stats-table">
            <div className="sd-stats-table-header">
              <span>الرقم</span>
              <span>التفاصيل</span>
              <span>آخر رسالة</span>
              <span>التاريخ</span>
            </div>
            {inquiries.slice(0, 5).map((conv) => (
              <div key={conv.id} className="sd-stats-table-row" onClick={() => onNavigate('conversations')}>
                <span className="sd-stats-order-id">#{conv.id}</span>
                <span className="sd-stats-order-product">{conv.specs || conv.tag || '—'}</span>
                <span className="sd-stats-last-msg">{lastMsg(conv)}</span>
                <span className="sd-stats-order-date">
                  {conv.createdAt ? new Date(conv.createdAt).toLocaleDateString('ar-SA') : '—'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Maintenance */}
      <div className="sd-stats-section">
        <div className="sd-stats-section-header">
          <h3>🔧 صيانة ({maintenance.length})</h3>
          <button className="sd-stats-view-all" onClick={() => onNavigate('conversations')}>عرض الكل ←</button>
        </div>
        {maintenance.length === 0 ? (
          <div className="sd-stats-empty">لا توجد طلبات صيانة</div>
        ) : (
          <div className="sd-stats-table">
            <div className="sd-stats-table-header">
              <span>الرقم</span>
              <span>التفاصيل</span>
              <span>الحالة</span>
              <span>التاريخ</span>
            </div>
            {maintenance.slice(0, 5).map((order) => (
              <div key={order.id} className="sd-stats-table-row" onClick={() => onNavigate('conversations')}>
                <span className="sd-stats-order-id">#{order.id}</span>
                <span className="sd-stats-order-product">{order.product || order.specs || '—'}</span>
                <span className={`sd-stats-order-status sd-status-${order.status}`}>
                  {STATUS_LABELS[order.status] || order.status}
                </span>
                <span className="sd-stats-order-date">
                  {order.createdAt ? new Date(order.createdAt).toLocaleDateString('ar-SA') : '—'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* General Conversations */}
      <div className="sd-stats-section">
        <div className="sd-stats-section-header">
          <h3>💬 محادثات عامة ({generalChats.length})</h3>
          <button className="sd-stats-view-all" onClick={() => onNavigate('conversations')}>عرض الكل ←</button>
        </div>
        {generalChats.length === 0 ? (
          <div className="sd-stats-empty">لا توجد محادثات عامة</div>
        ) : (
          <div className="sd-stats-table">
            <div className="sd-stats-table-header">
              <span>الرقم</span>
              <span>الموضوع</span>
              <span>آخر رسالة</span>
              <span>التاريخ</span>
            </div>
            {generalChats.slice(0, 5).map((conv) => (
              <div key={conv.id} className="sd-stats-table-row" onClick={() => onNavigate('conversations')}>
                <span className="sd-stats-order-id">#{conv.id}</span>
                <span className="sd-stats-order-product">{conv.tag || 'محادثة'}</span>
                <span className="sd-stats-last-msg">{lastMsg(conv)}</span>
                <span className="sd-stats-order-date">
                  {conv.createdAt ? new Date(conv.createdAt).toLocaleDateString('ar-SA') : '—'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="sd-stats-section">
        <h3>⚡ إجراءات سريعة</h3>
        <div className="sd-stats-actions">
          <button className="sd-stats-action" onClick={() => onNavigate('conversations')}>
            💬 المحادثات
          </button>
          <button className="sd-stats-action" onClick={() => onNavigate('orders')}>
            📋 الطلبات
          </button>
          <button className="sd-stats-action" onClick={() => onNavigate('menu')}>
            ⚙️ إعدادات القائمة
          </button>
        </div>
      </div>
    </div>
  );
}

export default StatsPage;
